export const LOCAL_PRINT_BRIDGE_URL = 'http://127.0.0.1:17891';

export interface LocalPrinter {
  Name: string;
  DriverName: string;
  PortName: string;
  PrinterStatus: string;
  Shared: boolean;
}

export interface CommandPrintJob {
  documentType?: 'order' | 'cash_cut' | 'inventory_count';
  printerName?: string;
  printerIp?: string;
  printerPort?: number;
  businessName: string;
  orderNumber: string;
  customerName: string;
  customerPhone?: string;
  publicOrderUrl?: string;
  table?: string;
  createdAt: string;
  contentWidthMm: 58 | 80;
  copies: number;
  items: Array<{ quantity: number; name: string; price: number; notes?: string; unit?: string }>;
  total: number;
  paymentMethod?: string;
  notes?: string;
  totalItems: number;
  operatorName?: string;
  openedAt?: string;
  closedAt?: string;
  sales?: Array<{ label: string; amount: number }>;
  cashTotal?: number;
  cardTotal?: number;
  creditTotal?: number;
}

export interface BusinessPrinterTarget {
  displayName: string;
  printerName?: string;
  printerIp?: string;
  printerPort?: number;
  contentWidthMm: 58 | 80;
  copies: number;
  autoPrint: boolean;
  publicOrderUrl?: string;
}

async function bridgeRequest<T>(
  path: string,
  init?: { method?: string; body?: string; headers?: Record<string, string> },
  timeoutMs = 5000,
): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(`${LOCAL_PRINT_BRIDGE_URL}${path}`, {
      ...init,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (reason) {
    if (reason instanceof DOMException && reason.name === 'AbortError') {
      throw new Error(`El puente local de impresión no respondió en ${Math.round(timeoutMs / 1000)} segundos. Abre FoodDash Print Bridge en la tablet.`);
    }
    throw reason;
  } finally {
    window.clearTimeout(timeout);
  }
  const payload = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? `El puente respondió con estado ${response.status}.`);
  return payload;
}

export const localPrintBridge = {
  health: () => bridgeRequest<{ ok: true; service: string; version: string; computer: string }>('/health'),
  printers: () => bridgeRequest<{ ok: true; printers: LocalPrinter[] }>('/printers'),
  // El puente responde cuando la impresora ya recibió el trabajo completo.
  // Una térmica por Wi-Fi puede tardar bastante más de cinco segundos al
  // imprimir dos copias; cancelar antes hace que la cola reenvíe el pedido.
  print: (job: CommandPrintJob) => bridgeRequest<{ ok: true; message: string; printer: string }>(
    '/print',
    { method: 'POST', body: JSON.stringify(job) },
    120_000,
  ),
};
